import React, { useEffect, useState } from 'react';
import { Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import EmailRecipientFields, { emptyRoutingState, routingApiPayload } from './EmailRecipientFields';

/**
 * Sammelversand: alle Zieladressen sichtbar, plus extra An/CC/BCC für jede Mail.
 */
export default function BulkEmailRecipientsDialog({
  open,
  onClose,
  onConfirm,
  confirming = false,
  loading = false,
  title,
  description = 'Bitte Empfänger prüfen. Weitere Adressen, CC und BCC gelten für jede einzelne Mail.',
  recipients = [],
  skipped = [],
  selectable = false,
  testId = 'bulk-email-recipients-dialog',
}) {
  const [routing, setRouting] = useState(emptyRoutingState());
  const [selected, setSelected] = useState([]);
  const ids = recipients.map((r, i) => r.id || r.email || String(i));

  useEffect(() => {
    if (open) setRouting(emptyRoutingState());
  }, [open]);

  useEffect(() => {
    if (open && selectable) setSelected(ids);
    // Empfängerliste einmal beim Öffnen übernehmen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectable, recipients]);

  const allOn = ids.length > 0 && ids.every((id) => selected.includes(id));
  const sendCount = selectable ? selected.length : recipients.length;
  const canSend = !loading && sendCount > 0 && !confirming;

  const toggleOne = (id) => {
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !confirming) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col" data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-8 text-center text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
              Empfänger werden geladen…
            </div>
          ) : (
            <>
              <div>
                <div className="flex items-center justify-between gap-3 mb-1">
                  <div className="text-xs font-medium text-slate-500">
                    An ({sendCount} {sendCount === 1 ? 'Empfänger' : 'Empfänger'})
                  </div>
                  {selectable && recipients.length > 0 && (
                    <label className="flex items-center gap-2 text-xs text-slate-600">
                      <input
                        type="checkbox"
                        checked={allOn}
                        onChange={() => setSelected(allOn ? [] : ids)}
                        data-testid={`${testId}-select-all`}
                      />
                      Alle auswählen
                    </label>
                  )}
                </div>
                {recipients.length === 0 ? (
                  <p className="text-sm text-amber-800" data-testid={`${testId}-empty`}>
                    Keine Empfänger mit hinterlegter E-Mail-Adresse.
                  </p>
                ) : (
                  <ul
                    className="max-h-48 overflow-y-auto rounded border border-slate-200 divide-y divide-slate-100 text-sm"
                    data-testid={`${testId}-list`}
                  >
                    {recipients.map((r, i) => {
                      const id = r.id || r.email || String(i);
                      return (
                        <li key={id} className="px-3 py-1.5 flex items-center justify-between gap-3">
                          {selectable && (
                            <input
                              type="checkbox"
                              checked={selected.includes(id)}
                              onChange={() => toggleOne(id)}
                              data-testid={`${testId}-recipient-${id}`}
                              aria-label={r.label || r.name || r.email}
                            />
                          )}
                          <span className="text-slate-800 truncate flex-1">{r.label || r.name || '—'}</span>
                          <span className="text-slate-600 shrink-0 font-mono text-xs">{r.email}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              {skipped?.length > 0 && (
                <div className="text-xs text-amber-800" data-testid={`${testId}-skipped`}>
                  Übersprungen ({skipped.length}):{' '}
                  {skipped.map((s) => {
                    const label = s.label || s.name || s.invoice_number || '—';
                    return s.reason ? `${label} (${s.reason})` : label;
                  }).join(', ')}
                </div>
              )}
              <EmailRecipientFields
                extraTo={routing.extraTo}
                cc={routing.cc}
                bcc={routing.bcc}
                onChange={setRouting}
                disabled={confirming || loading}
                testId={`${testId}-routing`}
              />
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0 shrink-0">
          <Button variant="outline" onClick={onClose} disabled={confirming}>Abbrechen</Button>
          <Button
            onClick={() => {
              const payload = routingApiPayload(routing);
              if (selectable) payload.profile_ids = selected;
              onConfirm(payload);
            }}
            disabled={!canSend}
            data-testid={`${testId}-confirm`}
          >
            {confirming ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
            {`An ${sendCount} Empfänger senden`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
